import { useMemo } from 'react';
import { Link } from 'react-router';

import { EmptyState, ErrorState, FilterBar, LoadingState } from '@/components/PageHeader';
import { EntryStatusPill } from '@/components/StatusPill';
import { Select } from '@/components/ui/input';
import { Card, Table, Th } from '@/components/ui/table';
import { useCompanyBasics } from '@/features/auth/session';
import { useJobContext } from '@/features/jobs/JobDetailPage';
import { fmtDateLong, fmtTime } from '@/lib/dates';
import { formatQuantity, hrs } from '@/lib/format';
import { sortRows, useTableSort } from '@/lib/sort';
import { useUrlState } from '@/lib/url-state';

import { useEntriesList } from './useEntries';

const DEFAULTS = { status: '', foreman: '', sort: '' };

/** W3 Entries tab: list by date desc with status, foreman, hours, quantities summary; click → W4. */
export function EntriesTab() {
  const { job } = useJobContext();
  const { tz } = useCompanyBasics();
  const [state, setState] = useUrlState(DEFAULTS);
  const sort = useTableSort(state.sort, (v) => setState({ sort: v }));
  const list = useEntriesList({
    jobId: job.id,
    status: state.status || undefined,
    foremanId: state.foreman || undefined,
  });
  const rows = useMemo(
    () =>
      sortRows(list.data?.items ?? [], sort.column ?? 'date', sort.column ? sort.dir : 'desc', {
        date: (e) => e.entryDate,
        foreman: (e) => e.foremanName,
        status: (e) => e.status,
        labor: (e) => e.laborHours,
        machine: (e) => e.machineHours,
        photos: (e) => e.photoCount,
      }),
    [list.data, sort.column, sort.dir],
  );

  return (
    <>
      <FilterBar>
        <Select
          aria-label="Status"
          value={state.status}
          onChange={(e) => setState({ status: e.target.value })}
          className="w-auto"
        >
          <option value="">Status · All</option>
          <option value="finished">Finished</option>
          <option value="synced">Synced</option>
          <option value="needs_review">Needs review</option>
          <option value="approved">Approved</option>
          <option value="draft">Draft</option>
        </Select>
        <Select
          aria-label="Foreman"
          value={state.foreman}
          onChange={(e) => setState({ foreman: e.target.value })}
          className="w-auto"
        >
          <option value="">Foreman · All</option>
          {job.foremen.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </Select>
      </FilterBar>
      {list.isPending ? (
        <LoadingState />
      ) : list.isError ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : (
        <Card>
          <Table>
            <thead>
              <tr>
                <Th sortKey="date" sort={sort}>
                  Date
                </Th>
                <Th sortKey="foreman" sort={sort}>
                  Foreman
                </Th>
                <Th sortKey="status" sort={sort}>
                  Status
                </Th>
                <Th>Quantities</Th>
                <Th right sortKey="labor" sort={sort}>
                  Labor h
                </Th>
                <Th right sortKey="machine" sort={sort}>
                  Machine h
                </Th>
                <Th right sortKey="photos" sort={sort}>
                  Photos
                </Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={8}>
                    <EmptyState>No entries yet.</EmptyState>
                  </td>
                </tr>
              ) : null}
              {rows.map((e) => (
                <tr key={e.id}>
                  <td>
                    <Link
                      to={`/entries/${e.id}`}
                      className="font-semibold text-orange-ink hover:underline"
                    >
                      {fmtDateLong(e.entryDate)}
                    </Link>
                    <span className="sub">
                      finished {fmtTime(e.finishedAt, tz)} · v{e.version}
                    </span>
                  </td>
                  <td>{e.foremanName}</td>
                  <td>
                    <EntryStatusPill status={e.status} />
                  </td>
                  <td className="num">
                    {e.quantitySummary
                      .map((q) => `${q.code} ${formatQuantity(q.quantity)} ${q.unit}`)
                      .join(' · ') || <span className="text-ink3">none</span>}
                  </td>
                  <td className="r">{hrs(e.laborHours)}</td>
                  <td className="r">{hrs(e.machineHours)}</td>
                  <td className="r">{e.photoCount}</td>
                  <td className="act">
                    <Link
                      to={`/entries/${e.id}`}
                      className="text-xs font-semibold text-orange-ink hover:underline"
                    >
                      Open
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}
    </>
  );
}
